import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Route as RouteIcon, Copy, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatShortLocation } from '@/utils/location';
import { getTypeShortLabel } from '@/constants/entityTypes';
import 'leaflet/dist/leaflet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLYLINE_OPTIONS = { color: '#FF0000', weight: 4, opacity: 0.7 };

const numberedIcon = (n) =>
  L.divIcon({
    className: 'shared-route-marker',
    html: `<div style="
      width: 32px; height: 32px;
      border-radius: 50% 50% 50% 0;
      background: #FF0000;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,.35);
      display:flex;align-items:center;justify-content:center;
    "><span style="transform: rotate(45deg); color: white; font-weight: 700;">${n}</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });

const SharedRoute = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchRoute = async () => {
      try {
        const res = await axios.get(`${API}/routes/share/${token}`);
        if (!cancelled) setRoute(res.data);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.status === 404
              ? 'This route link is invalid or has been revoked by its owner.'
              : 'Could not load this route.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchRoute();
    return () => { cancelled = true; };
  }, [token]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 text-center">
        <RouteIcon className="h-16 w-16 text-muted-foreground mb-3" />
        <h1 className="text-xl font-semibold mb-2">Route unavailable</h1>
        <p className="text-muted-foreground mb-6 max-w-md">{error}</p>
        <Button onClick={() => navigate('/')}>Go to Home</Button>
      </div>
    );
  }

  const stops = route.stops || [];
  const positions = stops
    .filter((s) => s.latitude != null && s.longitude != null)
    .map((s) => [s.latitude, s.longitude]);
  const center =
    positions.length > 0
      ? [
          positions.reduce((a, p) => a + p[0], 0) / positions.length,
          positions.reduce((a, p) => a + p[1], 0) / positions.length,
        ]
      : [39.5, -98.35];

  const miles =
    route.distance_km != null ? Math.round(route.distance_km * 0.621371) : null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <RouteIcon className="h-5 w-5 text-primary shrink-0" />
              <h1 className="text-lg font-semibold truncate" data-testid="shared-route-name">
                {route.name}
              </h1>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {route.owner_name && <span>Shared by {route.owner_name}</span>}
              <span>· {stops.length} stops</span>
              {miles != null && <span>· {miles} mi</span>}
              <span className="inline-flex items-center gap-1">
                <Eye className="h-3 w-3" /> {route.view_count}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="shared-copy-link-btn">
              <Copy className="h-3.5 w-3.5 mr-1" />
              Copy link
            </Button>
            <Button size="sm" onClick={() => navigate('/login')}>
              Plan your own
            </Button>
          </div>
        </div>
      </header>

      {/* Description */}
      {route.description && (
        <div className="max-w-6xl mx-auto px-4 py-3">
          <p className="text-sm text-muted-foreground" data-testid="shared-route-description">
            {route.description}
          </p>
        </div>
      )}

      {/* Map */}
      <div className="max-w-6xl mx-auto px-4 pb-4">
        <div className="h-[55vh] sm:h-[500px] rounded-2xl overflow-hidden border border-border shadow-sm">
          <MapContainer center={center} zoom={5} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {positions.length >= 2 && (
              <Polyline positions={positions} pathOptions={POLYLINE_OPTIONS} />
            )}
            {stops.map((s, i) =>
              s.latitude != null && s.longitude != null ? (
                <Marker
                  key={s.entity_id}
                  position={[s.latitude, s.longitude]}
                  icon={numberedIcon(i + 1)}
                >
                  <Popup>
                    <div className="min-w-[180px]">
                      <p className="font-semibold mb-1">{s.name}</p>
                      <p className="text-xs text-gray-600 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {formatShortLocation(s)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ) : null
            )}
          </MapContainer>
        </div>
      </div>

      {/* Stops list */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <h2 className="text-sm font-medium text-muted-foreground mb-2">Itinerary</h2>
        <ol className="space-y-2">
          {stops.map((s, i) => (
            <li
              key={s.entity_id}
              className="flex items-start gap-3 rounded-lg border border-border bg-card px-3 py-2"
              data-testid={`shared-stop-${i}`}
            >
              <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {formatShortLocation(s)}
                </p>
              </div>
              {s.entity_type && (
                <Badge variant="outline" className="text-xs shrink-0">
                  {getTypeShortLabel(s.entity_type)}
                </Badge>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default SharedRoute;
